import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { TCreativeGoal } from "domain/entities/creative-goal-response";
import { checkParamsCreativeOnly } from "helper/filtersHelper";

type TCreativeGoalProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TCreativeGoalState = {
  requestParamsIndex: number;
  creativeGoalData?: TCreativeGoal;
  downloadingReports?: boolean;
  downloadCreativeGoalReport: () => void;
  isLoading: boolean;
};

const initContext: TCreativeGoalState = {
  requestParamsIndex: 0,
  downloadCreativeGoalReport: () => null,
  isLoading: true,
};
export const CreativeGoalContext = createContext(initContext);

export const CreativeGoalProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TCreativeGoalProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [creativeGoalData, setCreativeGoalData] = useState<TCreativeGoal>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadCreativeGoalReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportCreativeGoalCSV(currentRequestParams)
      .then(createBlobDownloader("creative-goal.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsCreativeOnly);
    }

    return checkParamsCreativeOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getCreativeGoal(currentRequestParams)
        .then(setCreativeGoalData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <CreativeGoalContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        creativeGoalData,
        downloadCreativeGoalReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </CreativeGoalContext.Provider>
  );
};
